// 2024
// Author: Aaron Post

import { BehaviorData } from "../scenedata/behaviordata";

export interface ConfigData {
    src: string | null;
    color: string | null;
    prewarm: number;
    cameraRadius: number;
    alpha?: number | null;
    // optional blob for user-uploaded GLB
    blobGLB: string | null;
    mode?: string;
    /**
     * Environment (IBL) map for lighting and reflections. Viewer configuration
     * (the `environment-image` attribute), not scene data: no scene file
     * carries one.
     *
     * Unset means the shipped {@link DEFAULT_ENVIRONMENT_IMAGE_URL}; `''`
     * means no environment map at all. The attribute is read once, when the
     * viewer starts; a host changes it afterwards with
     * `TraceScene.setEnvironmentImage`, which writes this same field.
     */
    environmentImage?: string;
    // optional initial behaviors to apply to the implicit scene root (from behaviors attribute)
    behaviors?: BehaviorData[];
    /**
     * Whether to show a contact-shadow ground plane at y=0.
     * @default true
     */
    groundShadow?: boolean;
    /**
     * Height of the shadow ground plane in meters (scene-local Y).
     * When unset, defaults to slightly above y=0.
     */
    groundHeight?: number;
    /** Whether to show the built-in "View in AR" button */
    xr?: boolean;
    /**
     * Whether to start in play mode (interactions active) or edit mode
     * (selection active, no interactions). Defaults to true (play mode).
     */
    playMode?: boolean;
}

/**
 * The environment (IBL) map a viewer lights with while
 * {@link ConfigData.environmentImage} is unset.
 *
 * A published package has to light a scene for a consumer who configured
 * nothing, and the public CMS copy is the honest default for that. A
 * deployment serving its own storage — a local stack, an on-prem install —
 * sets the `environment-image` attribute to its own copy, and then the engine
 * never fetches from a cloud that deployment does not own.
 * `TraceScene.setEnvironmentImage` changes it later; a viewer started without
 * the attribute has already requested this url by then.
 */
export const DEFAULT_ENVIRONMENT_IMAGE_URL =
    'https://storageacceausprod.blob.core.windows.net/trace-cms-assets/environment/neutral.hdr';
